'use client';

import { type FlexProps } from '@ironhack/design-system2/components/flex';
import { lineHeightForSize } from '@ironhack/design-system2/utils';
import { type ReactElement, useEffect } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { useSessionStorage } from 'react-use';

import {
  Storytelling,
  type StorytellingCard,
} from '@/app/components/client/storytelling';
import { type VideoPlayerData } from '@/app/components/server/cms-video';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms';
import { type Campus, type WithTracking } from '@/types';
import { type HeaderFormProps } from '../ab--legacy-header-with-form/header-form';

export type LegacyPdpHeader = DatoBlock<
  {
    button: [DatoButtonBlock];
    description: StructuredTextGraphQlResponse;
    image: DatoImage | null;
    ironform: HeaderFormProps | null;
    subtitle: string;
    tag: string;
    title: string;
    video: VideoPlayerData | null;
  },
  'PdpPageHeaderRecord'
>;

type Props = {
  data: LegacyPdpHeader;
  params: { campus: Campus };
} & FlexProps;

export const LegacyPdpHeader = (props: Props): ReactElement | null => {
  const { data, params, ...flexProps } = props;
  const {
    button: [buttonData],
    description,
    image,
    ironform,
    subtitle,
    tag,
    title,
    video,
  } = data;
  const { campus: campusCode } = params;
  const [campus, setCampus] = useSessionStorage('campus', campusCode);

  useEffect(() => {
    if (campus !== campusCode) {
      setCampus(campusCode);
    }
  }, [campus, campusCode, setCampus]);

  const { isEnabled } = useFeatureToggle();
  if (isEnabled('pdp-hero-form') && ironform) return null;

  const button: [WithTracking<DatoButtonBlock>] = [
    { ...buttonData, tracking: { f: 'pd' } },
  ];
  const card: [StorytellingCard] = [{ content: description }];
  const moduleFormat: [DatoModuleFormat] = [
    {
      background: 'transparent',
      spacingBottom: ['32', null, '80'],
      spacingTop: ['32', null, '40'],
    },
  ];
  const storytellingData = {
    columns: [
      { button, card },
      { image, video },
    ],
    headingProps: {
      as: 'h1' as const,
      lineHeight: [lineHeightForSize('3xl'), null, lineHeightForSize('6xl')],
      noOfLines: [3, null, 2],
      textStyle: ['3xl', null, '6xl'],
    },
    moduleFormat,
    subHeadingProps: { as: 'h2' as const },
    subtitle,
    tag,
    title,
  };

  return <Storytelling as="header" data={storytellingData} {...flexProps} />;
};
